'use client'

import * as React from 'react'
import { Loader2 } from 'lucide-react'
import { UploadZone } from '../UploadZone'
import type { UploadCsvContent } from './index'

export interface UploadCsvPanelProps {
  content: UploadCsvContent
}

export function UploadCsvPanel({ content }: UploadCsvPanelProps) {
  const isLoading = content.uploadProps.isLoading || false

  return (
    <UploadZone
      accept={content.uploadProps.accept}
      onUploadComplete={content.uploadProps.onUploadComplete}
      onGetSigningUrl={content.uploadProps.onGetSigningUrl}
      isLoading={isLoading}
    >
      <div className="border border-dashed w-full border-muted-foreground/30 rounded-lg p-4 text-center hover:border-muted-foreground/50 transition-colors">
        <div className="space-y-4">
          <div className="space-y-2 text-center">
            <h3 className="font-semibold text-xs">{content.title}</h3>
            <p className="text-xs text-muted-foreground">
              {content.description}
            </p>
          </div>
          <button
            type="button"
            disabled={isLoading}
            className="inline-flex gap-2 items-center justify-center whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 leading-none bg-primary text-primary-foreground shadow hover:bg-primary/90 h-7 rounded-md px-2 text-xs font-normal ease-in"
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()
              let element: HTMLElement | null = e.currentTarget
              while (element && !element.querySelector('input[type="file"]')) {
                element = element.parentElement
              }
              const fileInput = element?.querySelector(
                'input[type="file"]',
              ) as HTMLInputElement
              if (fileInput) {
                fileInput.click()
              }
            }}
          >
            {isLoading ? (
              <>
                <Loader2 className="h-3 w-3 animate-spin" />
                Processing...
              </>
            ) : (
              content.buttonProps.label
            )}
          </button>
          {content.downloadExample && (
            <div className="pt-2">
              <button
                onClick={async () => {
                  if (content.downloadExample?.onDownload) {
                    await content.downloadExample.onDownload()
                  } else {
                    const blob = new Blob([content.downloadExample!.content], {
                      type: 'text/csv',
                    })
                    const url = URL.createObjectURL(blob)
                    const a = document.createElement('a')
                    a.href = url
                    a.download = content.downloadExample!.filename
                    document.body.appendChild(a)
                    a.click()
                    document.body.removeChild(a)
                    URL.revokeObjectURL(url)
                  }
                }}
                className="text-xs text-muted-foreground hover:text-foreground underline transition-colors"
              >
                {content.downloadExample.label || 'Download example file'}
              </button>
            </div>
          )}
        </div>
      </div>
    </UploadZone>
  )
}
